import React, { useState } from "react";
import styled from "styled-components";

// ── Timeline styled-components ─────────────────────────────────────

const TimelineWrapper = styled.div`
  overflow-x: auto;
  padding-bottom: 16px;
  margin-bottom: 48px;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-track { background: var(--nd-surface); }
  &::-webkit-scrollbar-thumb { background: var(--nd-border-visible); border-radius: 2px; }
`;

const LaneContainer = styled.div` min-width: 900px; `;

const Heading = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin-bottom: 16px;
`;

const Lane = styled.div` margin-bottom: 24px; `;

const LaneName = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-bottom: 12px;
`;

const LaneNodes = styled.div` display: flex; align-items: center; gap: 0; `;

const Arrow = styled.div`
  width: 40px;
  height: 1px;
  background: var(--nd-border-visible);
  position: relative;
  flex-shrink: 0;
  &::after {
    content: '>';
    position: absolute;
    right: -2px;
    top: -8px;
    font-family: 'Space Mono', monospace;
    font-size: 11px;
    color: var(--nd-text-disabled);
  }
`;

const NodeCard = styled.button`
  background: ${({ active }) => active ? "var(--nd-surface)" : "transparent"};
  border: 1px solid ${({ active }) => active ? "var(--nd-text-display)" : "var(--nd-border)"};
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  text-align: left;
  flex-shrink: 0;
  min-width: 140px;
  outline: none;
  transition: border-color 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  &:hover { border-color: var(--nd-text-secondary); }
`;

const NodeTitle = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin-bottom: 2px;
`;

const NodeCode = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  color: var(--nd-text-disabled);
  text-transform: uppercase;
`;

// ── Detail Panel styled-components ────────────────────────────────

const DetailPanel = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 32px;
  margin-top: 16px;
  margin-bottom: 48px;
`;

const DetailTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin-bottom: 4px;
`;

const DetailLane = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-top: 24px;
`;

const DetailBlock = styled.div``;

const DetailBlockLabel = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-bottom: 8px;
`;

const DetailBlockContent = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  line-height: 1.6;
`;

// ── Component ──────────────────────────────────────────────────────

/**
 * CareerLane — renders a single career lane's rung timeline and a
 * click-to-expand DetailPanel showing requirements/skills/experience/salary.
 *
 * Props:
 *   lane    — { key, name, nodes: [{title,code,requirements,skills,experience,salary}] }
 *   heading — optional string label rendered above the lane (e.g. "PROGRESSION")
 */
const CareerLane = ({ lane, heading }) => {
  const [activeNodeIdx, setActiveNodeIdx] = useState(null);

  const handleNodeClick = (nodeIdx) => {
    setActiveNodeIdx((prev) => (prev === nodeIdx ? null : nodeIdx));
  };

  const selectedNode = activeNodeIdx !== null ? lane.nodes[activeNodeIdx] : null;

  return (
    <>
      {heading && <Heading>{heading}</Heading>}
      <TimelineWrapper>
        <LaneContainer>
          <Lane>
            <LaneName>{lane.name}</LaneName>
            <LaneNodes>
              {lane.nodes.map((node, nodeIdx) => (
                <React.Fragment key={node.title}>
                  {nodeIdx > 0 && <Arrow />}
                  <NodeCard
                    active={activeNodeIdx === nodeIdx}
                    onClick={() => handleNodeClick(nodeIdx)}
                    aria-label={`View details for ${node.title}`}
                  >
                    <NodeTitle>{node.title}</NodeTitle>
                    <NodeCode>{node.code}</NodeCode>
                  </NodeCard>
                </React.Fragment>
              ))}
            </LaneNodes>
          </Lane>
        </LaneContainer>
      </TimelineWrapper>

      {selectedNode && (
        <DetailPanel>
          <DetailLane>{lane.name} TRACK</DetailLane>
          <DetailTitle>{selectedNode.title}</DetailTitle>
          <DetailGrid>
            <DetailBlock>
              <DetailBlockLabel>REQUIREMENTS</DetailBlockLabel>
              <DetailBlockContent>{selectedNode.requirements}</DetailBlockContent>
            </DetailBlock>
            <DetailBlock>
              <DetailBlockLabel>KEY SKILLS</DetailBlockLabel>
              <DetailBlockContent>{selectedNode.skills}</DetailBlockContent>
            </DetailBlock>
            <DetailBlock>
              <DetailBlockLabel>TYPICAL EXPERIENCE</DetailBlockLabel>
              <DetailBlockContent>{selectedNode.experience}</DetailBlockContent>
            </DetailBlock>
            <DetailBlock>
              <DetailBlockLabel>SALARY RANGE</DetailBlockLabel>
              <DetailBlockContent>{selectedNode.salary}</DetailBlockContent>
            </DetailBlock>
          </DetailGrid>
        </DetailPanel>
      )}
    </>
  );
};

export default CareerLane;
